/*
 * Communication Logs — v1 (Feb 2026)
 *
 * Single source of truth for monitoring / debugging / auditing all outbound
 * and inbound messages across every channel. Support engineers should be able
 * to determine WHY any message failed in under 30 seconds without opening
 * Mongo or server logs.
 *
 * Sections in this file (in order):
 *   1. System Health strip (5 real tiles + 2 "coming soon")
 *   2. Filters (Smart Search + 8 filters + failed-only chip)
 *   3. KPI cards (click-to-filter)
 *   4. Message Funnel
 *   5. Failure Analysis (horizontal bar chart)
 *   6. Log Table (paginated, sortable)
 *   7. Detail Drawer (recipient + communication + journey + provider + raw + actions)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import {
  Activity, RefreshCw, Download, X, Search, CheckCircle2, XCircle,
  Clock, ChevronRight, Copy, ExternalLink, MessageSquare, Radio,
} from "lucide-react";

// -------------------------------------------------------------------------
// Utilities
// -------------------------------------------------------------------------
const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};
const shortId = (s, n = 10) => (s ? String(s).slice(0, n) + "…" : "—");

const STAGE_LABEL = {
  user_loaded: "User Loaded",
  template_selected: "Campaign / Template Selected",
  params_generated: "Parameters Generated",
  request_built: "Request Built",
  sent_to_provider: "Sent to Provider",
  provider_accepted: "Provider Accepted",
  delivered: "Delivered",
  read: "Read / Opened",
  received_from_user: "Received from User",
  webhook_processed: "Webhook Processed",
};

const FAILURE_STAGE_LABEL = {
  user_load: "User Load",
  template_selection: "Template Selection",
  params_generation: "Parameter Generation",
  request_build: "Request Build",
  provider_send: "Provider Send (Transport)",
  provider_accept: "Provider Rejected",
  template_config_error: "Template Not Approved / Misconfigured",
  outside_24h_window: "Outside 24h Window",
  no_hsm_configured: "No Meta Template Configured",
  unknown: "Unknown",
};

// -------------------------------------------------------------------------
// System Health strip
// -------------------------------------------------------------------------
function HealthTile({ label, status }) {
  const ok = status?.ok;
  const color = ok === true ? "bg-emerald-500" : ok === false ? "bg-red-500" : "bg-slate-300";
  const text = ok === true ? "text-emerald-700" : ok === false ? "text-red-700" : "text-slate-500";
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 min-w-[170px]"
      data-testid={`health-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-slate-700">{label}</div>
        <div className={`text-[10px] truncate ${text}`}>{status?.detail || "—"}</div>
      </div>
    </div>
  );
}

function HealthPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/health/system");
      setHealth(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (loading && !health) {
    return <div className="text-xs text-slate-400 mb-3">Loading system health…</div>;
  }
  if (!health) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="comm-logs-health-panel">
      <HealthTile label="Backend" status={health.backend} />
      <HealthTile label="Scheduler" status={health.scheduler} />
      <HealthTile label="WhatsApp" status={health.whatsapp} />
      <HealthTile label="Email" status={health.email} />
      <HealthTile label="Webhook" status={health.webhook} />
      <HealthTile label="Meta Templates" status={health.meta_templates} />
      <HealthTile label="Queue" status={health.queue} />
    </div>
  );
}

// -------------------------------------------------------------------------
// KPI cards
// -------------------------------------------------------------------------
function KPICard({ label, value, tone, onClick, active }) {
  const toneMap = {
    total:    "text-slate-800",
    sent:     "text-blue-700",
    delivered:"text-cyan-700",
    read:     "text-emerald-700",
    pending:  "text-amber-700",
    failed:   "text-red-700",
    success:  "text-emerald-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-lg border px-3 py-2 bg-white hover:bg-slate-50 transition ${active ? "border-orange-400 ring-2 ring-orange-100" : "border-slate-200"}`}
      data-testid={`comm-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className={`text-xl font-bold ${toneMap[tone] || "text-slate-800"}`}>
        {value}
        {tone === "success" && <span className="text-xs font-normal ml-1">%</span>}
      </div>
    </button>
  );
}

// -------------------------------------------------------------------------
// Funnel (5-stage vertical bar list)
// -------------------------------------------------------------------------
function Funnel({ data }) {
  if (!data) return null;
  const rows = [
    { key: "created", label: "Created" },
    { key: "sent_to_provider", label: "Sent to Provider" },
    { key: "provider_accepted", label: "Provider Accepted" },
    { key: "delivered", label: "Delivered" },
    { key: "read", label: "Read / Opened" },
  ];
  const max = Math.max(1, ...rows.map((r) => data[r.key] || 0));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="comm-funnel">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-3">Communication Funnel</div>
      <div className="space-y-2">
        {rows.map((r) => {
          const v = data[r.key] || 0;
          const w = Math.max(4, Math.round((v / max) * 100));
          return (
            <div key={r.key} className="flex items-center gap-3">
              <div className="w-32 text-xs text-slate-600 shrink-0">{r.label}</div>
              <div className="flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400" style={{ width: `${w}%` }} />
              </div>
              <div className="w-14 text-right text-xs font-mono text-slate-700">{v}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Failure Analysis bar chart
// -------------------------------------------------------------------------
function FailureBar({ data, onPickStage }) {
  const rows = data || [];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="comm-failures">
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-3">Failure Analysis</div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-6 text-center">No failures in current filter range 🎉</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const w = Math.round((r.count / max) * 100);
            return (
              <button
                key={r.stage}
                type="button"
                onClick={() => onPickStage?.(r.stage)}
                className="w-full flex items-center gap-3 hover:bg-slate-50 rounded px-1 py-0.5 text-left"
                data-testid={`comm-failure-row-${r.stage}`}
              >
                <div className="w-52 text-xs text-slate-700 shrink-0 truncate">
                  {FAILURE_STAGE_LABEL[r.stage] || r.stage}
                </div>
                <div className="flex-1 h-3 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-red-500" style={{ width: `${w}%` }} />
                </div>
                <div className="w-10 text-right text-xs font-mono text-red-700">{r.count}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Log Table
// -------------------------------------------------------------------------
const STATUS_STYLES = {
  sent:      "bg-blue-50 text-blue-700 border-blue-200",
  delivered: "bg-cyan-50 text-cyan-700 border-cyan-200",
  read:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:    "bg-red-50 text-red-700 border-red-200",
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-50 text-slate-700 border-slate-200";
  return <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full border ${cls}`}>{status || "—"}</span>;
}

function SortHeader({ field, label, sort, onSortChange }) {
  const active = sort === field || sort === `-${field}`;
  const desc = sort === `-${field}`;
  return (
    <th
      className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 cursor-pointer select-none whitespace-nowrap"
      onClick={() => onSortChange?.(active && desc ? field : `-${field}`)}
      data-testid={`comm-sort-${field}`}
    >
      <span className={active ? "text-orange-600" : ""}>{label}</span>
      {active && <span className="ml-1 text-[9px]">{desc ? "↓" : "↑"}</span>}
    </th>
  );
}

function LogTable({ rows, sort, onSortChange, onRowClick, page, pages, total, size, onPageChange }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="comm-log-table">
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <SortHeader field="created_at" label="Time" sort={sort} onSortChange={onSortChange} />
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Channel</th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Dir</th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">User</th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">To</th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Template / Meta HSM</th>
              <SortHeader field="status" label="Status" sort={sort} onSortChange={onSortChange} />
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Furthest Stage</th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Failure</th>
              <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Provider ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-8 text-slate-400">No messages match the current filters.</td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className={`border-b border-slate-100 cursor-pointer ${
                  r.user?.escalated
                    ? "bg-red-50/70 hover:bg-red-100 border-l-4 border-l-red-500"
                    : "hover:bg-slate-50"
                }`}
                onClick={() => onRowClick(r.id)}
                data-testid={`comm-row-${r.id}`}
              >
                <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtTime(r.created_at)}</td>
                <td className="px-2 py-2 uppercase text-[10px] font-semibold text-slate-700">{r.channel}</td>
                <td className="px-2 py-2 text-[10px] uppercase text-slate-500">{r.direction}</td>
                <td className="px-2 py-2 text-slate-800 truncate max-w-[160px]" title={r.user?.name}>
                  <div className="flex items-center gap-1">
                    <span className="truncate">{r.user?.name || "—"}</span>
                    {r.user?.escalated && (
                      <span
                        className="inline-flex items-center gap-0.5 text-[8px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-600 text-white border border-red-700 shrink-0"
                        title="This recipient is currently flagged escalated."
                      >
                        Esc
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-2 py-2 text-slate-600 font-mono text-[10px] truncate max-w-[140px]">
                  {r.channel === "email" ? (r.user?.email || "—") : (r.user?.phone || "—")}
                </td>
                <td className="px-2 py-2 text-slate-700 truncate max-w-[220px]" title={r.template?.name}>
                  <div className="truncate">{r.template?.name || <span className="text-slate-400">ad-hoc</span>}</div>
                  {r.template?.meta_template_name && (
                    <div className="text-[9px] font-mono text-orange-600 truncate">HSM: {r.template.meta_template_name}</div>
                  )}
                </td>
                <td className="px-2 py-2"><StatusPill status={r.status} /></td>
                <td className="px-2 py-2 text-[10px] text-slate-600">{STAGE_LABEL[r.furthest_ok] || r.furthest_ok || "—"}</td>
                <td className="px-2 py-2 text-[10px] text-red-700 truncate max-w-[150px]" title={r.failure_reason}>
                  {r.failure_stage ? FAILURE_STAGE_LABEL[r.failure_stage] : (r.failure_reason ? "yes" : "—")}
                </td>
                <td className="px-2 py-2 font-mono text-[10px] text-slate-500 truncate max-w-[110px]" title={r.provider_message_id}>
                  {shortId(r.provider_message_id, 14)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-xs text-slate-600">
        <div>
          <span data-testid="comm-page-info">
            Showing {rows.length > 0 ? (page - 1) * size + 1 : 0}–
            {(page - 1) * size + rows.length} of {total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            data-testid="comm-page-prev"
          >Prev</button>
          <span>Page {page} / {pages || 1}</span>
          <button
            className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40"
            disabled={page >= pages}
            onClick={() => onPageChange(page + 1)}
            data-testid="comm-page-next"
          >Next</button>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Detail Drawer
// -------------------------------------------------------------------------
function JourneyStage({ stage }) {
  const ICON = {
    ok:       <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
    failed:   <XCircle className="h-4 w-4 text-red-500" />,
    pending:  <Clock className="h-4 w-4 text-slate-300" />,
    skipped:  <Clock className="h-4 w-4 text-slate-200" />,
  };
  const color = stage.status === "failed" ? "text-red-700" : stage.status === "ok" ? "text-slate-800" : "text-slate-400";
  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={`journey-${stage.name}`}>
      <div className="pt-0.5">{ICON[stage.status] || ICON.pending}</div>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-semibold ${color}`}>{STAGE_LABEL[stage.name] || stage.name}</div>
        {stage.at && <div className="text-[10px] text-slate-400">{fmtTime(stage.at)}</div>}
        {stage.details && <div className="text-[10px] text-slate-500 mt-0.5 break-words">{stage.details}</div>}
      </div>
    </div>
  );
}

function CollapsibleJSON({ label, obj, testId }) {
  const [open, setOpen] = useState(false);
  const json = JSON.stringify(obj || {}, null, 2);
  return (
    <div className="border border-slate-200 rounded bg-slate-50 mt-2" data-testid={testId}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-2 py-1.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-100"
        onClick={() => setOpen((v) => !v)}
      >
        <span>{label}</span>
        <ChevronRight className={`h-3 w-3 transition ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <pre className="text-[10px] font-mono text-slate-700 max-h-64 overflow-auto p-2 border-t border-slate-200 whitespace-pre-wrap break-all">
          {json}
        </pre>
      )}
    </div>
  );
}

function DetailDrawer({ messageId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/comm-logs/${messageId}`);
        if (!cancelled) setDetail(r.data);
      } catch (e) {
        toast.error("Could not load message details");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [messageId]);

  const copyCurl = () => {
    if (!detail) return;
    const req = detail.raw?.provider?.raw || {};
    const c = detail.communication || {};
    const body = c.provider_template_name
      ? JSON.stringify({
          messaging_product: "whatsapp",
          to: detail.recipient?.phone,
          type: "template",
          template: {
            name: c.provider_template_name,
            language: { code: c.language || "en" },
            components: [{
              type: "body",
              parameters: (detail.parameters_sent || []).map((p, i) => ({
                type: "text",
                ...(p.name ? { parameter_name: p.name } : {}),
                text: `<<${p.source || `arg${i + 1}`}>>`,
              })),
            }],
          },
        }, null, 2)
      : JSON.stringify(req, null, 2);
    const curl =
`curl -X POST https://graph.facebook.com/v19.0/<PHONE_NUMBER_ID>/messages \\
  -H "Authorization: Bearer <ACCESS_TOKEN>" \\
  -H "Content-Type: application/json" \\
  -d '${body}'`;
    navigator.clipboard.writeText(curl);
    toast.success("cURL copied — replace <PHONE_NUMBER_ID> and <ACCESS_TOKEN>");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} data-testid="comm-drawer">
      <div
        className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Message Detail</div>
            <div className="text-sm font-semibold text-slate-800">{messageId?.slice(0, 8)}…</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="comm-drawer-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
        {!loading && detail && (
          <div className="p-4 space-y-5">
            {/* Recipient */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Recipient</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-slate-500">Name:</span> <span className="font-medium">{detail.recipient?.name || "—"}</span></div>
                <div><span className="text-slate-500">User ID:</span> <span className="font-mono text-[10px]">{detail.recipient?.id || "—"}</span></div>
                <div><span className="text-slate-500">Phone:</span> <span className="font-mono">{detail.recipient?.phone || "—"}</span></div>
                <div><span className="text-slate-500">Email:</span> <span className="font-mono truncate">{detail.recipient?.email || "—"}</span></div>
                <div><span className="text-slate-500">Cadence:</span> <span>{detail.communication?.cadence || "—"}</span></div>
                <div><span className="text-slate-500">Step ID:</span> <span className="font-mono text-[10px]">{detail.communication?.cadence_step_id || "—"}</span></div>
              </div>
            </section>

            {/* Communication */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Communication</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-slate-500">Channel:</span> <span className="font-medium uppercase">{detail.communication?.channel}</span></div>
                <div><span className="text-slate-500">Provider:</span> <span>{detail.communication?.provider}</span></div>
                <div><span className="text-slate-500">Template:</span> <span>{detail.communication?.template_name || "—"}</span></div>
                <div><span className="text-slate-500">Meta HSM:</span> <span className="font-mono text-orange-600">{detail.communication?.provider_template_name || "—"}</span></div>
                <div><span className="text-slate-500">Language:</span> <span>{detail.communication?.language || "—"}</span></div>
                <div><span className="text-slate-500">Mode:</span> <span>{detail.communication?.mode || "—"}</span></div>
              </div>
            </section>

            {/* Parameters */}
            {(detail.parameters_sent || []).length > 0 && (
              <section>
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Parameters Sent</div>
                <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs font-mono">
                  {detail.parameters_sent.map((p, i) => (
                    <div key={i} data-testid={`param-${i}`}>
                      {p.name ? <span className="text-slate-800">{p.name}</span> : <span className="text-slate-500">{`{{${i + 1}}}`}</span>}
                      {" = "}
                      <span className="text-orange-600">{p.source || "?"}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Journey */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Message Journey</div>
              <div className="rounded border border-slate-200 bg-white p-2 divide-y divide-slate-100">
                {(detail.journey || []).map((s, i) => <JourneyStage key={`${s.name}-${i}`} stage={s} />)}
              </div>
              {detail.failure_stage && (
                <div className="mt-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px]">
                  <div className="font-semibold text-red-700">Failure: {FAILURE_STAGE_LABEL[detail.failure_stage] || detail.failure_stage}</div>
                  {detail.failure_reason && <div className="text-red-600 mt-0.5 break-words">{detail.failure_reason}</div>}
                </div>
              )}
            </section>

            {/* Provider Response */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Provider Response</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <div><span className="text-slate-500">Message ID:</span> <span className="font-mono text-[10px] break-all">{detail.provider_response?.message_id || "—"}</span></div>
                <div><span className="text-slate-500">Error Code:</span> <span className="font-mono">{detail.provider_response?.error_code || "—"}</span></div>
                <div className="col-span-2"><span className="text-slate-500">Error Title:</span> <span>{detail.provider_response?.error_title || "—"}</span></div>
                <div className="col-span-2"><span className="text-slate-500">Error Details:</span> <span>{detail.provider_response?.error_details || "—"}</span></div>
                <div><span className="text-slate-500">Request At:</span> <span className="font-mono text-[10px]">{fmtTime(detail.provider_response?.request_timestamp)}</span></div>
                <div><span className="text-slate-500">Response At:</span> <span className="font-mono text-[10px]">{fmtTime(detail.provider_response?.response_timestamp)}</span></div>
              </div>
            </section>

            {/* Raw */}
            <section>
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Raw Data</div>
              <CollapsibleJSON label="Provider payload" obj={detail.raw?.provider} testId="raw-provider" />
              <CollapsibleJSON label="Meta error" obj={detail.raw?.meta_error} testId="raw-meta-error" />
              <CollapsibleJSON label="Full ai_meta" obj={detail.raw?.ai_meta} testId="raw-ai-meta" />
            </section>

            {/* Actions */}
            <section className="border-t border-slate-100 pt-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-2">Actions</div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={copyCurl}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-white hover:bg-slate-50"
                  data-testid="comm-action-copy-curl"
                >
                  <Copy className="h-3.5 w-3.5" />
                  Copy as cURL
                </button>
                {detail.recipient?.id && (
                  <a
                    href={`/users/${detail.recipient.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-white hover:bg-slate-50"
                    data-testid="comm-action-open-user"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open User Profile
                  </a>
                )}
                {detail.recipient?.id && (
                  <a
                    href={`/users/${detail.recipient.id}#conversation`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-white hover:bg-slate-50"
                    data-testid="comm-action-view-conversation"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    View Conversation
                  </a>
                )}
                <button
                  disabled
                  title="Retry — coming in v2"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                  data-testid="comm-action-retry"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  Retry (v2)
                </button>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Main page
// -------------------------------------------------------------------------
export default function CommunicationLogs() {
  const [filters, setFilters] = useState({
    channel: "", direction: "", status: "", failure_stage: "",
    since: "", until: "", search: "", failed_only: false,
  });
  const [page, setPage] = useState(1);
  const [size] = useState(50);
  const [sort, setSort] = useState("-created_at");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [openId, setOpenId] = useState(null);

  const params = useMemo(() => {
    const p = { page, size, sort };
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== false && v != null) p[k] = v;
    });
    return p;
  }, [filters, page, size, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [logRes, sumRes] = await Promise.all([
        api.get("/comm-logs", { params }),
        api.get("/comm-logs/summary", { params: {
          channel: filters.channel, direction: filters.direction,
          since: filters.since, until: filters.until,
        }}),
      ]);
      setRows(logRes.data.rows || []);
      setTotal(logRes.data.total || 0);
      setPages(logRes.data.pages || 1);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error("Failed to load communication logs");
    } finally { setLoading(false); }
  }, [params, filters.channel, filters.direction, filters.since, filters.until]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const clearFilters = () => {
    setFilters({ channel: "", direction: "", status: "", failure_stage: "",
      since: "", until: "", search: "", failed_only: false });
    setPage(1);
  };

  const exportCSV = () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== false && v != null) qs.set(k, v);
    });
    const url = `${API_BASE}/comm-logs/export.csv?${qs.toString()}`;
    const token = localStorage.getItem("phc_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `comm_logs_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="comm-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Activity className="h-5 w-5 text-orange-500" />
            Communication Logs
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Single source of truth for every outbound & inbound message. Click a row to trace a message end-to-end.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600" data-testid="comm-auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-white hover:bg-slate-50"
            data-testid="comm-refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={exportCSV}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded border border-slate-200 bg-white hover:bg-slate-50"
            data-testid="comm-export-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      <HealthPanel />

      {/* Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 mb-4" data-testid="comm-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              placeholder="Search — name, phone, email, body, error…"
              value={filters.search}
              onChange={(e) => setF("search", e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
              data-testid="comm-filter-search"
            />
          </div>
          <select value={filters.channel} onChange={(e) => setF("channel", e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white" data-testid="comm-filter-channel">
            <option value="">All channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="push">Push</option>
          </select>
          <select value={filters.direction} onChange={(e) => setF("direction", e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white" data-testid="comm-filter-direction">
            <option value="">All directions</option>
            <option value="outbound">Outbound</option>
            <option value="inbound">Inbound</option>
          </select>
          <select value={filters.status} onChange={(e) => setF("status", e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white" data-testid="comm-filter-status">
            <option value="">All statuses</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="read">Read</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
          <select value={filters.failure_stage} onChange={(e) => setF("failure_stage", e.target.value)}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white" data-testid="comm-filter-failstage">
            <option value="">All failure stages</option>
            {Object.entries(FAILURE_STAGE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <input type="date" value={filters.since?.slice(0, 10) || ""}
            onChange={(e) => setF("since", e.target.value ? `${e.target.value}T00:00:00Z` : "")}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white" data-testid="comm-filter-since" />
          <input type="date" value={filters.until?.slice(0, 10) || ""}
            onChange={(e) => setF("until", e.target.value ? `${e.target.value}T23:59:59Z` : "")}
            className="px-2 py-1.5 text-xs border border-slate-200 rounded-md bg-white" data-testid="comm-filter-until" />
          <button
            onClick={() => setF("failed_only", !filters.failed_only)}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded border ${filters.failed_only ? "bg-red-50 border-red-300 text-red-700" : "border-slate-200 text-slate-600"}`}
            data-testid="comm-filter-failed-only"
          >
            <XCircle className="h-3.5 w-3.5" />
            Failed only
          </button>
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-700"
            data-testid="comm-clear-filters"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
          <KPICard label="Total" value={summary.kpi.total} tone="total"
            onClick={() => setF("status", "")} active={!filters.status && !filters.failed_only} />
          <KPICard label="Sent" value={summary.kpi.sent} tone="sent"
            onClick={() => setF("status", "sent")} active={filters.status === "sent"} />
          <KPICard label="Delivered" value={summary.kpi.delivered} tone="delivered"
            onClick={() => setF("status", "delivered")} active={filters.status === "delivered"} />
          <KPICard label="Read" value={summary.kpi.read} tone="read"
            onClick={() => setF("status", "read")} active={filters.status === "read"} />
          <KPICard label="Pending" value={summary.kpi.pending} tone="pending" onClick={() => {}} />
          <KPICard label="Failed" value={summary.kpi.failed} tone="failed"
            onClick={() => setF("failed_only", !filters.failed_only)} active={filters.failed_only} />
          <KPICard label="Success" value={summary.kpi.success_rate} tone="success" onClick={() => {}} />
        </div>
      )}

      {/* Funnel + Failure Analysis side-by-side */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
          <Funnel data={summary.funnel} />
          <FailureBar
            data={summary.failures_by_stage}
            onPickStage={(stage) => setF("failure_stage", stage)}
          />
        </div>
      )}

      {/* Log Table */}
      <LogTable
        rows={rows}
        sort={sort}
        onSortChange={setSort}
        onRowClick={setOpenId}
        page={page}
        pages={pages}
        total={total}
        size={size}
        onPageChange={setPage}
      />

      {openId && <DetailDrawer messageId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
